/**
 * ChallengePage.tsx — 5분 챌린지
 *
 * 흐름:
 *  1. 대기 화면  → "5분만 해볼까요?" CTA
 *  2. 타이머 화면 → 카운트다운 원형 프로그레스 + 문제 카드 순환
 *  3. 완료 화면  → 축하 애니메이션 + XP 획득 + 공유 문구
 *
 * 특징:
 *  - 300초(5분) 카운트다운, 10초마다 동기부여 메시지 교체
 *  - 중간에 개념 퀴즈 3문제 (간단한 O/X)
 *  - 완료 시 confetti 효과 (CSS 애니메이션)
 *  - 오늘 이미 완료했으면 결과 화면 바로 표시
 */

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import {
  ArrowLeft, Play, Pause, RotateCcw, ChevronRight,
  CheckCircle, XCircle, Zap, Clock, Star, Trophy, Flame
} from 'lucide-react'
import { useProgressStore } from '../store/progressStore'
import { getCurrentUnit } from '../data/syllabus'

// ── 현재 학습 단원 기반 링크 헬퍼 ──────────────────────────────────
function currentUnitPath(): string {
  const u = getCurrentUnit()
  if (!u) return '/syllabus'
  return u.contentPath ?? `/unit/${u.id}`
}
function currentUnitLabel(): string {
  const u = getCurrentUnit()
  return u ? `${u.id} · ${u.title}` : '다음 단원'
}

// ── 상수 ──────────────────────────────────────────────────────────
const CHALLENGE_SEC = 300   // 5분
const STORAGE_KEY = 'challenge_done_'
const todayKey = () => STORAGE_KEY + new Date().toISOString().slice(0, 10)

// ── 동기부여 메시지 (10초마다 교체) ──────────────────────────────
const BOOST_MESSAGES = [
  { emoji: '💪', text: '잘 하고 있어요! 계속 가세요.' },
  { emoji: '🔥', text: '이미 시작했다는 것 자체가 대단해요!' },
  { emoji: '🌱', text: '딱 5분이면 돼요. 거의 다 왔어요.' },
  { emoji: '⚡', text: '지금 이 순간이 수포자를 탈출하는 순간이에요!' },
  { emoji: '🎯', text: '집중! 나머지는 나중에 생각해요.' },
  { emoji: '📈', text: '오늘 1% 성장 중입니다. 느껴지나요?' },
  { emoji: '🏆', text: '완료하면 XP가 기다리고 있어요!' },
  { emoji: '✨', text: '수학이 두렵지 않다는 걸 증명하는 중이에요.' },
]

// ── 미니 퀴즈 풀 (OX) — 난이도별 20문제 ────────────────────────
type Difficulty = 'easy' | 'medium' | 'hard'
interface Quiz {
  q: string
  answer: boolean
  explain: string
  difficulty: Difficulty
}

const QUIZ_POOL: Quiz[] = [
  // ── 쉬움 (easy) ── 8문제
  {
    q: 'y = 2x + 3 에서 기울기는 2이다.',
    answer: true,
    explain: '맞아요! y = ax + b에서 a가 기울기예요. 여기선 a = 2.',
    difficulty: 'easy',
  },
  {
    q: '기울기가 음수(-)이면 그래프는 오른쪽으로 내려간다.',
    answer: true,
    explain: '맞아요! 기울기가 음수면 x가 커질수록 y가 작아져요. 📉',
    difficulty: 'easy',
  },
  {
    q: 'y = 3x 는 원점(0, 0)을 지난다.',
    answer: true,
    explain: '맞아요! y절편이 0이면 항상 원점을 지나요.',
    difficulty: 'easy',
  },
  {
    q: 'x = 0 일 때 y값을 "기울기"라고 한다.',
    answer: false,
    explain: '아니에요! x=0일 때 y값은 "y절편"이에요. 기울기는 x가 1 늘 때 y 변화량이에요.',
    difficulty: 'easy',
  },
  {
    q: 'y = x 의 기울기는 1이다.',
    answer: true,
    explain: '맞아요! y = 1·x + 0 이므로 기울기는 1이에요.',
    difficulty: 'easy',
  },
  {
    q: '일차함수 y = ax + b 에서 b는 y절편이다.',
    answer: true,
    explain: '맞아요! b는 그래프가 y축과 만나는 점의 y값이에요.',
    difficulty: 'easy',
  },
  {
    q: 'y = 5 는 일차함수이다.',
    answer: false,
    explain: '아니에요! y = 5는 x가 없는 상수함수예요. 일차함수는 x 항이 있어야 해요.',
    difficulty: 'easy',
  },
  {
    q: '함수 y = -3x + 1 에서 y절편은 1이다.',
    answer: true,
    explain: '맞아요! y = ax + b에서 b = 1이 y절편이에요.',
    difficulty: 'easy',
  },

  // ── 보통 (medium) ── 7문제
  {
    q: 'y = -x + 5 는 감소하는 일차함수이다.',
    answer: true,
    explain: '맞아요! 기울기 -1 < 0 이므로 x가 커질수록 y가 감소해요.',
    difficulty: 'medium',
  },
  {
    q: '기울기가 같은 두 일차함수의 그래프는 반드시 평행하다.',
    answer: false,
    explain: '아니에요! y절편도 같으면 같은 직선이 돼요. 기울기가 같고 y절편이 달라야 평행해요.',
    difficulty: 'medium',
  },
  {
    q: '두 점 (0, 1)과 (1, 3)을 지나는 직선의 기울기는 2이다.',
    answer: true,
    explain: '맞아요! 기울기 = (3-1)/(1-0) = 2/1 = 2 예요.',
    difficulty: 'medium',
  },
  {
    q: 'y = 2x + 1 과 y = 2x - 3 은 서로 평행하다.',
    answer: true,
    explain: '맞아요! 기울기가 둘 다 2로 같고, y절편이 다르므로 평행해요.',
    difficulty: 'medium',
  },
  {
    q: 'x절편이 2인 일차함수는 점 (0, 2)를 지난다.',
    answer: false,
    explain: '아니에요! x절편이 2라는 건 점 (2, 0)을 지난다는 뜻이에요. y = 0일 때 x = 2예요.',
    difficulty: 'medium',
  },
  {
    q: '일차함수 y = 3x - 6 의 x절편은 2이다.',
    answer: true,
    explain: '맞아요! y = 0을 대입하면 0 = 3x - 6, x = 2예요.',
    difficulty: 'medium',
  },
  {
    q: '기울기가 0이면 그래프는 x축에 평행한 수평선이다.',
    answer: true,
    explain: '맞아요! 기울기 = 0이면 y = b(상수)로 x축에 평행해요.',
    difficulty: 'medium',
  },

  // ── 어려움 (hard) ── 5문제
  {
    q: '일차함수 y = ax + b에서 a < 0, b < 0 이면 그래프는 1, 2, 4사분면을 지난다.',
    answer: false,
    explain: '아니에요! a < 0(우하향), b < 0(y절편 음수)이면 2, 3, 4사분면을 지나요.',
    difficulty: 'hard',
  },
  {
    q: '두 점 (2, 5)와 (4, 9)를 지나는 직선의 y절편은 1이다.',
    answer: true,
    explain: '맞아요! 기울기 = (9-5)/(4-2) = 2, y = 2x + b에 (2,5) 대입 → b = 1이에요.',
    difficulty: 'hard',
  },
  {
    q: 'y = -2x + 4 와 y = (1/2)x + 1 은 서로 수직으로 만난다.',
    answer: true,
    explain: '맞아요! 수직 조건: 두 기울기의 곱 = -1. (-2) × (1/2) = -1 ✅',
    difficulty: 'hard',
  },
  {
    q: '일차함수 y = (a-1)x + 3 이 상수함수가 되려면 a = 0 이어야 한다.',
    answer: false,
    explain: '아니에요! 상수함수는 x계수 = 0이어야 해요. a - 1 = 0 → a = 1 이에요.',
    difficulty: 'hard',
  },
  {
    q: '점 (3, 7)을 지나고 기울기가 2인 직선의 식은 y = 2x + 1이다.',
    answer: true,
    explain: '맞아요! y = 2x + b에 (3,7) 대입 → 7 = 6 + b → b = 1. y = 2x + 1 ✅',
    difficulty: 'hard',
  },
]

// 난이도별로 1문제씩(쉬움·보통·어려움) 랜덤 선택 → 총 3문제
function pickQuizzes(): Quiz[] {
  const pick = (diff: Difficulty) => {
    const pool = QUIZ_POOL.filter(q => q.difficulty === diff)
    return pool[Math.floor(Math.random() * pool.length)]
  }
  return [pick('easy'), pick('medium'), pick('hard')]
}

// ── 원형 타이머 SVG ──────────────────────────────────────────────
function CircleTimer({ remaining, total, isRunning }: {
  remaining: number
  total: number
  isRunning: boolean
}) {
  const R = 70
  const CIRC = 2 * Math.PI * R
  const pct = remaining / total
  const offset = CIRC * (1 - pct)

  const mins = Math.floor(remaining / 60)
  const secs = remaining % 60

  const color =
    pct > 0.6 ? '#10b981' :
    pct > 0.3 ? '#f59e0b' :
    '#ef4444'

  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={180} height={180} viewBox="0 0 180 180">
        {/* 배경 원 */}
        <circle cx={90} cy={90} r={R} fill="none" stroke="#e2e8f0" strokeWidth={10} />
        {/* 진행 원 */}
        <circle
          cx={90} cy={90} r={R}
          fill="none"
          stroke={color}
          strokeWidth={10}
          strokeLinecap="round"
          strokeDasharray={CIRC}
          strokeDashoffset={offset}
          transform="rotate(-90 90 90)"
          style={{ transition: 'stroke-dashoffset 1s linear, stroke 0.5s' }}
        />
      </svg>
      {/* 중앙 텍스트 */}
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-4xl font-black tabular-nums" style={{ color }}>
          {String(mins).padStart(2, '0')}:{String(secs).padStart(2, '0')}
        </span>
        <span className="text-xs text-slate-400 mt-1">
          {isRunning ? '진행 중' : remaining === total ? '시작 대기' : '일시정지'}
        </span>
      </div>
    </div>
  )
}

// ── Confetti CSS 애니메이션 ──────────────────────────────────────
function Confetti() {
  const pieces = Array.from({ length: 30 }, (_, i) => ({
    id: i,
    color: ['#f59e0b', '#10b981', '#6366f1', '#ec4899', '#0ea5e9'][i % 5],
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 1.5}s`,
    duration: `${1.5 + Math.random() * 1.5}s`,
    size: `${6 + Math.random() * 8}px`,
  }))

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {pieces.map(p => (
        <div
          key={p.id}
          className="absolute rounded-sm"
          style={{
            left: p.left,
            top: '-10px',
            width: p.size,
            height: p.size,
            backgroundColor: p.color,
            animation: `confettiFall ${p.duration} ${p.delay} ease-in forwards`,
          }}
        />
      ))}
      <style>{`
        @keyframes confettiFall {
          0%   { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(110vh) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  )
}

// ── 미니 퀴즈 카드 ───────────────────────────────────────────────
const DIFF_META: Record<Difficulty, { label: string; emoji: string; border: string; badge: string; xp: number }> = {
  easy:   { label: '쉬움',   emoji: '🌱', border: 'border-emerald-200', badge: 'bg-emerald-100 text-emerald-700', xp: 10 },
  medium: { label: '보통',   emoji: '⚡', border: 'border-amber-200',   badge: 'bg-amber-100   text-amber-700',   xp: 20 },
  hard:   { label: '어려움', emoji: '🔥', border: 'border-red-200',     badge: 'bg-red-100     text-red-700',     xp: 30 },
}

function QuizCard({
  quiz,
  onAnswer,
}: {
  quiz: Quiz
  onAnswer: (correct: boolean) => void
}) {
  const [answered, setAnswered] = useState<boolean | null>(null)
  const meta = DIFF_META[quiz.difficulty]

  const handle = (userAnswer: boolean) => {
    if (answered !== null) return
    const correct = userAnswer === quiz.answer
    setAnswered(userAnswer)
    setTimeout(() => onAnswer(correct), 1200)
  }

  return (
    <div className={`rounded-2xl border-2 ${meta.border} bg-white p-5 shadow-sm`}>
      {/* 헤더: 난이도 배지 + XP 안내 */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-black px-2.5 py-1 rounded-full ${meta.badge}`}>
            {meta.emoji} {meta.label}
          </span>
          <span className="text-xs text-slate-400 font-bold">⚡ 빠른 퀴즈</span>
        </div>
        <span className="text-xs font-black text-indigo-500">정답 시 +{meta.xp} XP</span>
      </div>
      <p className="text-sm font-bold text-slate-800 leading-relaxed mb-4">{quiz.q}</p>
      <div className="flex gap-3">
        {[true, false].map(val => {
          const isSelected = answered === val
          const isCorrect = val === quiz.answer
          let cls = 'flex-1 py-3 rounded-xl font-black text-sm transition-all '
          if (answered === null) {
            cls += val
              ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
              : 'bg-red-400 hover:bg-red-500 text-white'
          } else {
            if (isCorrect) cls += 'bg-emerald-500 text-white scale-105'
            else if (isSelected) cls += 'bg-red-400 text-white opacity-60'
            else cls += 'bg-slate-100 text-slate-400'
          }
          return (
            <button key={String(val)} onClick={() => handle(val)} className={cls}>
              {val ? '⭕ 맞다' : '❌ 아니다'}
            </button>
          )
        })}
      </div>
      {answered !== null && (
        <div className={`mt-3 rounded-xl p-3 text-xs font-bold ${answered === quiz.answer ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
          {answered === quiz.answer ? `✅ 정답! +${meta.xp} XP` : '❌ 오답!'} {quiz.explain}
        </div>
      )}
    </div>
  )
}

// ── 메인 페이지 ─────────────────────────────────────────────────
type Phase = 'ready' | 'running' | 'done'

export default function ChallengePage() {
  const [phase, setPhase] = useState<Phase>(() => {
    try {
      const saved = localStorage.getItem(todayKey())
      if (saved) return 'done'
    } catch { /* noop */ }
    return 'ready'
  })

  const [remaining, setRemaining] = useState(CHALLENGE_SEC)
  const [isRunning, setIsRunning] = useState(false)
  const [boostIdx, setBoostIdx] = useState(0)
  const [quizIdx, setQuizIdx] = useState(0)
  const [correctCount, setCorrectCount] = useState(0)
  const [showQuiz, setShowQuiz] = useState(false)
  const [earnedXp, setEarnedXp] = useState(0)

  // 챌린지 시작 시 랜덤으로 3문제 뽑아서 고정
  const selectedQuizzesRef = useRef<Quiz[]>(pickQuizzes())

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const boostRef = useRef<ReturnType<typeof setInterval> | null>(null)
  // 퀴즈는 elapsed 기준으로 판단 — 60초, 120초, 180초 경과 시
  const QUIZ_AT = [60, 120, 180]
  const quizShownRef = useRef<Set<number>>(new Set())

  const { data } = useProgressStore()
  const totalXp = data.totalXp ?? 0

  // 완료 처리
  const finish = useCallback((xp: number) => {
    clearInterval(intervalRef.current!)
    clearInterval(boostRef.current!)
    setPhase('done')
    setEarnedXp(xp)
    try { localStorage.setItem(todayKey(), JSON.stringify({ xp, completedAt: new Date().toISOString() })) } catch { /* noop */ }
  }, [])

  // 타이머 tick — 매 초마다 elapsed 확인해서 퀴즈 트리거
  useEffect(() => {
    if (!isRunning) return
    intervalRef.current = setInterval(() => {
      setRemaining(prev => {
        const elapsed = CHALLENGE_SEC - prev + 1
        // 퀴즈 트리거: 60·120·180초 경과 시점
        const hit = QUIZ_AT.find(t => elapsed === t && !quizShownRef.current.has(t))
        if (hit !== undefined) {
          quizShownRef.current.add(hit)
          setShowQuiz(true)
          setIsRunning(false)  // 퀴즈 동안 타이머 일시정지
        }
        if (prev <= 1) {
          setIsRunning(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
    return () => clearInterval(intervalRef.current!)
  }, [isRunning])

  // 완료 감지 (remaining === 0)
  useEffect(() => {
    if (remaining === 0 && phase === 'running') {
      const xp = 80 + correctCount * 20
      finish(xp)
    }
  }, [remaining, phase, correctCount, finish])

  // 동기부여 메시지 교체 (10초마다)
  useEffect(() => {
    if (!isRunning) return
    boostRef.current = setInterval(() => {
      setBoostIdx(i => (i + 1) % BOOST_MESSAGES.length)
    }, 10000)
    return () => clearInterval(boostRef.current!)
  }, [isRunning])

  const handleStart = () => {
    setIsRunning(true)
    setPhase('running')
  }

  const handlePause = () => setIsRunning(p => !p)

  const handleReset = () => {
    clearInterval(intervalRef.current!)
    clearInterval(boostRef.current!)
    selectedQuizzesRef.current = pickQuizzes()  // 재시작 시 새 문제 뽑기
    quizShownRef.current = new Set()
    setRemaining(CHALLENGE_SEC)
    setIsRunning(false)
    setPhase('ready')
    setBoostIdx(0)
    setQuizIdx(0)
    setCorrectCount(0)
    setShowQuiz(false)
  }

  const handleQuizAnswer = (correct: boolean) => {
    if (correct) setCorrectCount(c => c + 1)
    setShowQuiz(false)
    setQuizIdx(i => i + 1)
    setIsRunning(true)   // 타이머 재개
  }

  const boost = BOOST_MESSAGES[boostIdx]
  const elapsed = CHALLENGE_SEC - remaining
  const elapsedPct = Math.round((elapsed / CHALLENGE_SEC) * 100)

  // ── 완료 화면 ────────────────────────────────────────────────
  if (phase === 'done') {
    const savedRaw = (() => { try { return JSON.parse(localStorage.getItem(todayKey()) ?? '{}') } catch { return {} } })()
    const xp = earnedXp || savedRaw.xp || 80
    return (
      <div className="p-6 max-w-md mx-auto space-y-5 relative">
        <Confetti />

        <Link to="/" className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700">
          <ArrowLeft size={15} /> 대시보드로
        </Link>

        {/* 축하 카드 */}
        <div className="rounded-3xl overflow-hidden shadow-2xl text-white text-center"
          style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)' }}>
          <div className="pt-10 pb-6 px-6">
            <div className="text-7xl mb-3">🏆</div>
            <p className="text-xs font-bold tracking-widest text-white/70 uppercase mb-1">챌린지 완료!</p>
            <h2 className="text-3xl font-black mb-2">5분 해냈어요! 🎉</h2>
            <p className="text-white/80 text-sm leading-relaxed">
              오늘 5분을 투자한 당신,<br />수포자 탈출에 한 걸음 더 가까워졌어요.
            </p>
          </div>

          {/* XP 획득 */}
          <div className="mx-6 mb-6 bg-white/15 rounded-2xl p-4">
            <p className="text-xs text-white/60 mb-1">오늘 획득한 챌린지 XP</p>
            <p className="text-4xl font-black text-yellow-300">+{xp} XP</p>
            <p className="text-xs text-white/50 mt-1">퀴즈 정답 {correctCount}개 포함</p>
          </div>

          {/* 스탯 */}
          <div className="grid grid-cols-3 gap-px bg-white/10 border-t border-white/10">
            {[
              { label: '집중 시간', value: '5분', emoji: '⏱️' },
              { label: '퀴즈 정답', value: `${correctCount}개`, emoji: '✅' },
              { label: '연속 학습', value: `오늘`, emoji: '🔥' },
            ].map((s, i) => (
              <div key={i} className="py-4 bg-black/10">
                <div className="text-2xl">{s.emoji}</div>
                <div className="text-base font-black mt-1">{s.value}</div>
                <div className="text-xs text-white/50">{s.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* 공유 문구 */}
        <div className="rounded-2xl bg-slate-800 text-white p-4">
          <p className="text-xs text-slate-400 mb-2">📣 오늘의 한 마디</p>
          <p className="text-sm font-bold leading-relaxed">
            "오늘 5분 수학했어요. 내일도 할 거예요. 기울기처럼 조금씩 올라가고 있어요. 📈"
          </p>
        </div>

        {/* CTA 버튼들 */}
        <div className="space-y-3">
          <Link
            to={currentUnitPath()}
            className="flex items-center justify-between w-full rounded-2xl p-4 text-white shadow"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}
          >
            <div>
              <p className="font-black text-sm">개념 학습 이어가기</p>
              <p className="text-white/70 text-xs">{currentUnitLabel()} — XP 더 쌓기 → 레벨업</p>
            </div>
            <ChevronRight size={18} />
          </Link>
          <Link
            to="/growth"
            className="flex items-center justify-between w-full rounded-2xl p-4 bg-emerald-500 text-white shadow"
          >
            <div>
              <p className="font-black text-sm">내 성장 확인하기</p>
              <p className="text-white/70 text-xs">오늘 달력에 ✓ 표시됐어요</p>
            </div>
            <ChevronRight size={18} />
          </Link>
        </div>

        {/* 내일 다시 오기 */}
        <p className="text-center text-xs text-slate-400">
          내일 또 5분 챌린지가 기다리고 있어요 👋
        </p>
      </div>
    )
  }

  // ── 타이머 화면 ──────────────────────────────────────────────
  if (phase === 'running') {
    return (
      <div className="p-6 max-w-md mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <button onClick={handleReset} className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700">
            <ArrowLeft size={15} /> 포기하기
          </button>
          <span className="text-xs text-slate-400">{elapsedPct}% 완료</span>
        </div>

        {/* 타이머 */}
        <div className="flex flex-col items-center py-4">
          <CircleTimer remaining={remaining} total={CHALLENGE_SEC} isRunning={isRunning} />

          {/* 일시정지/재개 */}
          <button
            onClick={handlePause}
            className="mt-4 flex items-center gap-2 px-6 py-3 rounded-2xl text-white font-black text-sm shadow-lg"
            style={{ background: isRunning ? '#6366f1' : '#10b981' }}
          >
            {isRunning ? <><Pause size={16} /> 잠깐 쉬기</> : <><Play size={16} /> 계속하기</>}
          </button>
        </div>

        {/* 동기부여 메시지 */}
        <div className="rounded-2xl bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-100 p-4 text-center">
          <div className="text-3xl mb-1">{boost.emoji}</div>
          <p className="text-sm font-bold text-indigo-700">{boost.text}</p>
        </div>

        {/* 진행률 바 */}
        <div>
          <div className="flex justify-between text-xs text-slate-400 mb-1.5">
            <span>진행률</span>
            <span>{elapsedPct}%</span>
          </div>
          <div className="h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{
                width: `${elapsedPct}%`,
                background: 'linear-gradient(90deg, #10b981, #6366f1)',
              }}
            />
          </div>
        </div>

        {/* 퀴즈 카드 (일시정지 중 표시) */}
        {showQuiz && quizIdx < selectedQuizzesRef.current.length && (
          <QuizCard quiz={selectedQuizzesRef.current[quizIdx]} onAnswer={handleQuizAnswer} />
        )}

        {/* 남은 퀴즈 예고 */}
        {!showQuiz && (
          <div className="rounded-xl bg-amber-50 border border-amber-100 p-3 flex items-center gap-2">
            <Zap size={14} className="text-amber-500 flex-shrink-0" />
            <p className="text-xs text-amber-700">
              1분마다 퀴즈가 나와요! 맞히면 <strong>+20 XP</strong> 추가
            </p>
          </div>
        )}

        {/* 즉시 완료 버튼 (개발/테스트용은 아니고, 5분 다 됐다 느끼면) */}
        <button
          onClick={() => finish(80 + correctCount * 20)}
          className="w-full py-3 rounded-2xl border border-slate-200 text-slate-500 text-sm hover:bg-slate-50 transition-colors"
        >
          ✅ 이미 5분 다 했어요 → 완료 처리
        </button>
      </div>
    )
  }

  // ── 대기 화면 ────────────────────────────────────────────────
  return (
    <div className="p-6 max-w-md mx-auto space-y-6">
      <Link to="/" className="flex items-center gap-1.5 text-slate-500 text-sm hover:text-slate-700">
        <ArrowLeft size={15} /> 대시보드로
      </Link>

      {/* 헤더 */}
      <div className="text-center py-4">
        <div className="text-6xl mb-3">⏱️</div>
        <h1 className="text-3xl font-black text-slate-800 mb-2">5분 챌린지</h1>
        <p className="text-slate-500 text-sm leading-relaxed">
          딱 5분이에요. 타이머 켜고 수학과 함께해보세요.<br />
          <span className="text-indigo-600 font-bold">시작하면 멈출 수 없을지도 몰라요 😊</span>
        </p>
      </div>

      {/* 혜택 카드 */}
      <div className="rounded-2xl border border-indigo-100 overflow-hidden">
        <div className="p-5 text-white" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
          <p className="font-black text-base mb-3">5분 동안 이런 일이 생겨요</p>
          <div className="space-y-2">
            {[
              { emoji: '⏱️', text: '5분 집중 타이머 카운트다운' },
              { emoji: '⚡', text: '60초마다 OX 퀴즈 — 맞히면 +20 XP' },
              { emoji: '💬', text: '10초마다 동기부여 메시지' },
              { emoji: '🏆', text: '완료 시 기본 80 XP + 보너스' },
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-2 text-sm text-white/90">
                <span className="text-lg">{item.emoji}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-indigo-50 p-3 text-center">
          <p className="text-xs text-indigo-500 font-bold">
            총 최대 <span className="text-indigo-700 text-base">140 XP</span> 획득 가능 (기본 80 + 퀴즈 3개 × 20)
          </p>
        </div>
      </div>

      {/* 오늘 이미 완료 여부 */}
      {(() => {
        try {
          const saved = localStorage.getItem(todayKey())
          if (!saved) return null
          const { xp } = JSON.parse(saved)
          return (
            <div className="rounded-2xl bg-emerald-50 border border-emerald-200 p-4 text-center">
              <CheckCircle size={24} className="text-emerald-500 mx-auto mb-1" />
              <p className="text-sm font-black text-emerald-700">오늘 이미 완료했어요! 🎉</p>
              <p className="text-xs text-emerald-500 mt-0.5">획득 XP: {xp} XP</p>
              <button
                onClick={() => setPhase('done')}
                className="mt-2 text-xs text-emerald-600 underline"
              >결과 다시 보기</button>
            </div>
          )
        } catch { return null }
      })()}

      {/* 시작 버튼 */}
      <button
        onClick={handleStart}
        className="w-full py-5 rounded-2xl text-white font-black text-xl shadow-xl hover:opacity-90 active:scale-95 transition-all flex items-center justify-center gap-3"
        style={{ background: 'linear-gradient(135deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)' }}
      >
        <Play size={24} fill="white" /> 5분 챌린지 시작!
      </button>

      {/* 수포자 공감 문구 */}
      <div className="rounded-2xl bg-slate-800 text-white p-5">
        <p className="text-xs text-slate-400 mb-2">💬 수포자였다가 탈출한 선배의 말</p>
        <p className="text-sm font-bold leading-relaxed italic">
          "처음엔 5분도 버티기 힘들었어요. 근데 5분이 10분 되고, 10분이 30분 되더라고요.
          시작만 하면 돼요. 진짜로."
        </p>
        <p className="text-xs text-slate-500 mt-2 text-right">— 수포자 탈출 성공 선배</p>
      </div>

      {/* 다른 동기부여 도구 */}
      <div className="grid grid-cols-2 gap-3">
        <Link to="/growth"
          className="rounded-xl border border-slate-200 p-3 text-center hover:bg-slate-50 transition-colors">
          <div className="text-2xl">📈</div>
          <p className="text-xs font-bold text-slate-700 mt-1">1% 성장 보기</p>
        </Link>
        <Link to="/real-calc"
          className="rounded-xl border border-slate-200 p-3 text-center hover:bg-slate-50 transition-colors">
          <div className="text-2xl">🧮</div>
          <p className="text-xs font-bold text-slate-700 mt-1">실생활 계산기</p>
        </Link>
      </div>
    </div>
  )
}
